"use client";

import { Image as ImageType } from "@/types/Image";
import Image from "next/image";
import { ReactNode, useEffect, useState } from "react";
import PaginationDots from "../PaginationDots";
import "./index.scss";

type CardCarousel = {
  backgroundImageId: string;
  logoImageId?: string;
  content: ReactNode;
};

export default function CardCarouselClient({
  cards,
  imagesById,
}: {
  cards: CardCarousel[];
  imagesById: Record<string, ImageType>;
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  useEffect(() => {
    const updateCardsPerPage = () => {
      if (window.innerWidth < 768) {
        setCardsPerPage(1);
      } else {
        setCardsPerPage(3);
      }
    };

    updateCardsPerPage();
    window.addEventListener("resize", updateCardsPerPage);
    return () => window.removeEventListener("resize", updateCardsPerPage);
  }, []);

  const totalPages = Math.ceil(cards.length / cardsPerPage);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.innerWidth < 768) {
      setTouchStartX(e.changedTouches[0].clientX);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.innerWidth >= 768 || touchStartX === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const swipeDistance = touchStartX - touchEndX;
    const minSwipeDistance = 50;

    if (swipeDistance > minSwipeDistance) {
      // Swiped left
      goToPage(currentPage + 1);
    } else if (swipeDistance < -minSwipeDistance) {
      // Swiped right
      goToPage(currentPage - 1);
    }

    setTouchStartX(null);
  };

  const pageNodes = [];

  for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
    const isLastPage = pageIndex === totalPages - 1;
    const numberOfEmptyCards =
      isLastPage && cards.length % cardsPerPage !== 0
        ? cardsPerPage - (cards.length % cardsPerPage)
        : 0;

    const cardNodes = cards
      .slice(pageIndex * cardsPerPage, (pageIndex + 1) * cardsPerPage)
      .map((card) => {
        const logoImage = card.logoImageId && imagesById[card.logoImageId];
        const backgroundImage = imagesById[card.backgroundImageId];

        return (
          <div
            key={`${card.backgroundImageId}-${card.logoImageId}`}
            className="card"
          >
            <div className="card-content">
              {backgroundImage && (
                <Image
                  src={backgroundImage.src}
                  alt={backgroundImage.alt}
                  fill
                  className="background-image"
                />
              )}

              <div className="logo-wrapper">
                {logoImage && (
                  <Image
                    src={logoImage.src}
                    alt={logoImage.alt}
                    width={logoImage.width}
                    height={logoImage.height}
                  />
                )}
              </div>

              <div className="content">
                <p>{card.content}</p>
              </div>
            </div>
          </div>
        );
      });

    const emptyCardNodes = Array.from(
      { length: numberOfEmptyCards },
      (_, i) => (
        <div key={`empty-${i}`} className="card empty">
          <div className="card-content"></div>
        </div>
      )
    );

    pageNodes.push(
      <div key={pageIndex} className="page-container">
        {cardNodes}
        {emptyCardNodes}
      </div>
    );
  }

  return (
    <div className="component card-carousel">
      <div className="relative">
        <div className="card-carousel-window">
          <div
            className="card-carousel-inner-window"
            style={{ transform: `translateX(-${currentPage * 100}%)` }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {pageNodes}
          </div>
        </div>
      </div>
      <PaginationDots
        numberOfPages={totalPages}
        goToPage={goToPage}
        currentPage={currentPage}
      />
    </div>
  );
}
